import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLORS } from "../../../helpers/designConstants";
import { Button } from "../../../components/standardElements";
import ConfirmModal from "../../../components/ConfirmModal";
import { getFunctions, httpsCallable } from "firebase/functions";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${COLORS.secondary};
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.active ? COLORS.primary : COLORS.text};
  border-bottom: 2px solid ${props => props.active ? COLORS.primary : 'transparent'};
  transition: all 0.2s;
  min-height: 44px;
  
  &:hover {
    color: ${COLORS.primary};
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
    min-height: 40px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
    min-height: 36px;
  }
`;

const Badge = styled.span`
  background: ${props => props.type === 'pending' ? '#fef3c7' : '#ecfdf5'};
  color: ${props => props.type === 'pending' ? '#d97706' : '#059669'};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
`;

const DuplicateCard = styled.div`
  background: white;
  border: 1px solid ${COLORS.secondary};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 8px;
  }
`;

const DuplicateHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const DuplicateTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.primary};
`;

const DuplicateIds = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const IdChip = styled.span`
  background: ${props => props.suggested ? '#eff6ff' : '#f3f4f6'};
  color: ${props => props.suggested ? '#2563eb' : '#6b7280'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  font-weight: 500;
  border: 1px solid ${props => props.suggested ? '#bfdbfe' : '#d1d5db'};
`;

const MetaInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  color: ${COLORS.text};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 6px;
    font-size: 12px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MetaLabel = styled.span`
  font-weight: 500;
`;

const MetaValue = styled.span`
  color: ${COLORS.primary};
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid ${COLORS.secondary};
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const ActionButton = styled(Button)`
  min-width: 80px;
  padding: 8px 16px;
  font-size: 14px;
  
  @media (max-width: 480px) {
    min-width: 100%;
    padding: 10px 16px;
  }
`;

const CustomMappingForm = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLORS.secondary};
  border-radius: 6px;
  padding: 16px;
  margin: 12px 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.text};
  margin-bottom: 4px;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${COLORS.secondary};
  border-radius: 4px;
  font-size: 14px;
  color: ${COLORS.primary};
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${COLORS.text};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${COLORS.text};
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #059669;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const BatchActionsBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid ${COLORS.secondary};
  border-radius: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    padding: 12px;
  }
`;

const BatchSelectionInfo = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.text};
  min-width: 150px;
`;

const BatchActionButton = styled(Button)`
  padding: 8px 16px;
  font-size: 14px;
  min-width: auto;

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-right: 12px;

  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    margin-right: 8px;
  }
`;

const DuplicateManagement = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingDuplicates, setPendingDuplicates] = useState([]);
  const [approvedDuplicates, setApprovedDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [customMappings, setCustomMappings] = useState({});
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadPendingDuplicates = async () => {
    try {
      const functions = getFunctions();
      const getPendingDuplicates = httpsCallable(functions, "duplicateManagement-getPendingDuplicates");
      const result = await getPendingDuplicates();
      setPendingDuplicates(result.data.data || []);
    } catch (error) {
      console.error("Error loading pending duplicates:", error);
      setError("Failed to load pending duplicates");
    }
  };

  const loadApprovedDuplicates = async () => {
    try {
      const functions = getFunctions();
      const getApprovedDuplicates = httpsCallable(functions, "duplicateManagement-getApprovedDuplicates");
      const result = await getApprovedDuplicates();
      setApprovedDuplicates(result.data.data || []);
    } catch (error) {
      console.error("Error loading approved duplicates:", error);
      setError("Failed to load approved duplicates");
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadPendingDuplicates(),
        loadApprovedDuplicates()
      ]);
    } catch (error) {
      setError("Failed to load duplicate data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAction = async (duplicateKey, action, correctID = null, duplicateID = null) => {
    setActionInProgress(duplicateKey);
    
    try {
      const functions = getFunctions();
      
      if (action === 'delete') {
        // Delete pending duplicate
        const deletePendingDuplicate = httpsCallable(functions, "duplicateManagement-deletePendingDuplicate");
        await deletePendingDuplicate({ duplicateKey });
      } else if (action === 'remove') {
        // Remove approved duplicate
        const removeApprovedDuplicate = httpsCallable(functions, "duplicateManagement-removeApprovedDuplicate");
        await removeApprovedDuplicate({ duplicateKey });
      } else {
        // Approve/reject pending duplicate
        const updatePendingDuplicate = httpsCallable(functions, "duplicateManagement-updatePendingDuplicate");
        await updatePendingDuplicate({
          duplicateKey,
          action,
          correctID,
          duplicateID
        });
      }
      
      // Reload data to reflect changes
      await loadData();
      setShowConfirmModal(false);
      setModalData(null);
      setCustomMappings(prev => {
        const updated = { ...prev };
        delete updated[duplicateKey];
        return updated;
      });
      
    } catch (error) {
      console.error(`Error ${action}ing duplicate:`, error);
      setError(`Failed to ${action} duplicate`);
    } finally {
      setActionInProgress(null);
    }
  };

  const confirmAction = (duplicate, action) => {
    const mapping = customMappings[duplicate.key];
    const correctID = mapping?.correctID || duplicate.suggestedCorrectID;
    const duplicateID = mapping?.duplicateID || duplicate.suggestedDuplicateID;

    setModalData({
      duplicate,
      action,
      correctID,
      duplicateID
    });
    setShowConfirmModal(true);
  };

  const handleBatchAction = async (action) => {
    if (selectedDuplicates.size === 0) return;

    setBatchProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const operations = Array.from(selectedDuplicates).map(duplicateKey => {
        const duplicate = pendingDuplicates.find(d => d.key === duplicateKey);
        const mapping = customMappings[duplicateKey];
        return {
          duplicateKey,
          action,
          correctID: mapping?.correctID || duplicate.suggestedCorrectID,
          duplicateID: mapping?.duplicateID || duplicate.suggestedDuplicateID
        };
      });

      // Split into chunks of 50 (Firebase function limit)
      const CHUNK_SIZE = 50;
      const chunks = [];
      for (let i = 0; i < operations.length; i += CHUNK_SIZE) {
        chunks.push(operations.slice(i, i + CHUNK_SIZE));
      }

      const functions = getFunctions();
      const batchUpdate = httpsCallable(functions, "duplicateManagement-batchUpdatePendingDuplicates");

      // Process chunks sequentially
      let totalSuccessCount = 0;
      let totalFailureCount = 0;
      const allSuccessfulKeys = [];
      const allFailedKeys = [];

      for (let i = 0; i < chunks.length; i++) {
        if (chunks.length > 1) {
          setSuccessMessage(`Processing chunk ${i + 1} of ${chunks.length}...`);
        }

        const result = await batchUpdate({ operations: chunks[i] });

        totalSuccessCount += result.data.successCount;
        totalFailureCount += result.data.failureCount;

        const successfulKeys = result.data.results
          .filter(r => r.success)
          .map(r => r.duplicateKey);

        const failedKeys = result.data.results
          .filter(r => !r.success)
          .map(r => r.duplicateKey);

        allSuccessfulKeys.push(...successfulKeys);
        allFailedKeys.push(...failedKeys);
      }

      // Remove successfully processed duplicates from local state (optimistic update)
      setPendingDuplicates(prev =>
        prev.filter(dup => !allSuccessfulKeys.includes(dup.key))
      );

      // Clear selections and custom mappings for processed items
      setSelectedDuplicates(new Set());
      setCustomMappings(prev => {
        const updated = { ...prev };
        allSuccessfulKeys.forEach(key => delete updated[key]);
        return updated;
      });

      // Show success message
      setSuccessMessage(
        `Batch operation completed: ${totalSuccessCount} successful, ${totalFailureCount} failed`
      );

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      if (totalFailureCount > 0) {
        setError(`Some operations failed: ${allFailedKeys.join(", ")}`);
      }

    } catch (error) {
      console.error("Error in batch operation:", error);
      setError(`Failed to process batch operation: ${error.message}`);
    } finally {
      setBatchProcessing(false);
    }
  };

  const confirmBatchAction = (action) => {
    const actionText = action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Delete';
    setModalData({
      isBatch: true,
      action,
      count: selectedDuplicates.size
    });
    setShowConfirmModal(true);
  };

  const toggleDuplicateSelection = (key) => {
    setSelectedDuplicates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDuplicates.size === pendingDuplicates.length) {
      setSelectedDuplicates(new Set());
    } else {
      setSelectedDuplicates(new Set(pendingDuplicates.map(d => d.key)));
    }
  };

  const updateCustomMapping = (duplicateKey, field, value) => {
    setCustomMappings(prev => ({
      ...prev,
      [duplicateKey]: {
        ...prev[duplicateKey],
        [field]: value
      }
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const renderPendingDuplicates = () => {
    if (pendingDuplicates.length === 0) {
      return (
        <EmptyState>
          <p>No pending duplicates found</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            New duplicates will appear here when detected during data parsing
          </p>
        </EmptyState>
      );
    }

    return (
      <>
        <BatchActionsBar>
          <BatchSelectionInfo>
            {selectedDuplicates.size} of {pendingDuplicates.length} selected
          </BatchSelectionInfo>
          <BatchActionButton
            onClick={toggleSelectAll}
            style={{ backgroundColor: COLORS.primary }}
          >
            {selectedDuplicates.size === pendingDuplicates.length ? 'Deselect All' : 'Select All'}
          </BatchActionButton>
          <BatchActionButton
            onClick={() => confirmBatchAction('approve')}
            disabled={selectedDuplicates.size === 0 || batchProcessing}
            style={{ backgroundColor: COLORS.green }}
          >
            {batchProcessing ? 'Processing...' : `Approve Selected (${selectedDuplicates.size})`}
          </BatchActionButton>
          <BatchActionButton
            onClick={() => confirmBatchAction('reject')}
            disabled={selectedDuplicates.size === 0 || batchProcessing}
            style={{ backgroundColor: COLORS.alertRed }}
          >
            {batchProcessing ? 'Processing...' : `Reject Selected (${selectedDuplicates.size})`}
          </BatchActionButton>
        </BatchActionsBar>

        {pendingDuplicates.map(duplicate => {
      const mapping = customMappings[duplicate.key] || {};
      const correctID = mapping.correctID || duplicate.suggestedCorrectID;
      const duplicateID = mapping.duplicateID || duplicate.suggestedDuplicateID;
      
      return (
        <DuplicateCard key={duplicate.key}>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              checked={selectedDuplicates.has(duplicate.key)}
              onChange={() => toggleDuplicateSelection(duplicate.key)}
            />
            <DuplicateTitle>Duplicate Pattern Detected</DuplicateTitle>
          </CheckboxWrapper>
          
          <DuplicateIds>
            {duplicate.detectedDuplicates.map(id => (
              <IdChip 
                key={id} 
                suggested={id === duplicate.suggestedCorrectID}
              >
                {id}
                {id === duplicate.suggestedCorrectID && " (suggested correct)"}
              </IdChip>
            ))}
          </DuplicateIds>

          <MetaInfo>
            <MetaItem>
              <MetaLabel>First Detected:</MetaLabel>
              <MetaValue>{formatTimestamp(duplicate.firstDetected)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Last Seen:</MetaLabel>
              <MetaValue>{formatTimestamp(duplicate.lastSeen)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Detection Count:</MetaLabel>
              <MetaValue>{duplicate.detectionCount}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Status:</MetaLabel>
              <MetaValue style={{ textTransform: 'capitalize' }}>
                {duplicate.status}
              </MetaValue>
            </MetaItem>
          </MetaInfo>

          <CustomMappingForm>
            <FormRow>
              <div>
                <FormLabel>Correct ID:</FormLabel>
                <FormSelect
                  value={correctID}
                  onChange={(e) => updateCustomMapping(duplicate.key, 'correctID', e.target.value)}
                >
                  {duplicate.detectedDuplicates.map(id => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </FormSelect>
              </div>
              <div>
                <FormLabel>Duplicate ID:</FormLabel>
                <FormSelect
                  value={duplicateID}
                  onChange={(e) => updateCustomMapping(duplicate.key, 'duplicateID', e.target.value)}
                >
                  {duplicate.detectedDuplicates.filter(id => id !== correctID).map(id => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </FormSelect>
              </div>
            </FormRow>
          </CustomMappingForm>

          <ActionBar>
            <ActionButton
              onClick={() => confirmAction(duplicate, 'approve')}
              disabled={actionInProgress === duplicate.key}
              style={{ backgroundColor: COLORS.green }}
            >
              {actionInProgress === duplicate.key ? 'Processing...' : 'Approve'}
            </ActionButton>
            <ActionButton
              onClick={() => confirmAction(duplicate, 'reject')}
              disabled={actionInProgress === duplicate.key}
              style={{ backgroundColor: COLORS.alertRed }}
            >
              {actionInProgress === duplicate.key ? 'Processing...' : 'Reject'}
            </ActionButton>
            <ActionButton
              onClick={() => confirmAction(duplicate, 'delete')}
              disabled={actionInProgress === duplicate.key}
              style={{ backgroundColor: '#6b7280', color: 'white' }}
            >
              {actionInProgress === duplicate.key ? 'Processing...' : 'Delete'}
            </ActionButton>
          </ActionBar>
        </DuplicateCard>
      );
    })}
      </>
    );
  };

  const renderApprovedDuplicates = () => {
    if (approvedDuplicates.length === 0) {
      return (
        <EmptyState>
          <p>No approved duplicates found</p>
        </EmptyState>
      );
    }

    return approvedDuplicates.map(duplicate => (
      <DuplicateCard key={duplicate.key}>
        <DuplicateHeader>
          <DuplicateTitle>Approved Duplicate Mapping</DuplicateTitle>
        </DuplicateHeader>
        
        <DuplicateIds>
          <IdChip suggested>{duplicate.correctID} (correct)</IdChip>
          <IdChip>{duplicate.duplicateID} (duplicate)</IdChip>
        </DuplicateIds>

        <MetaInfo>
          <MetaItem>
            <MetaLabel>Date Added:</MetaLabel>
            <MetaValue>{formatTimestamp(duplicate.dateAdded)}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Status:</MetaLabel>
            <MetaValue style={{ textTransform: 'capitalize' }}>
              {duplicate.status}
            </MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Lookup Key:</MetaLabel>
            <MetaValue style={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {duplicate.key}
            </MetaValue>
          </MetaItem>
        </MetaInfo>

        <ActionBar>
          <ActionButton
            onClick={() => confirmAction(duplicate, 'remove')}
            disabled={actionInProgress === duplicate.key}
            style={{ backgroundColor: COLORS.alertRed }}
          >
            {actionInProgress === duplicate.key ? 'Processing...' : 'Remove'}
          </ActionButton>
        </ActionBar>
      </DuplicateCard>
    ));
  };

  if (loading) {
    return <LoadingState>Loading duplicate management data...</LoadingState>;
  }

  return (
    <Container>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

      <TabContainer>
        <Tab 
          active={activeTab === "pending"} 
          onClick={() => setActiveTab("pending")}
        >
          Pending Duplicates
          <Badge type="pending">{pendingDuplicates.length}</Badge>
        </Tab>
        <Tab 
          active={activeTab === "approved"} 
          onClick={() => setActiveTab("approved")}
        >
          Approved Duplicates
          <Badge type="approved">{approvedDuplicates.length}</Badge>
        </Tab>
      </TabContainer>

      {activeTab === "pending" ? renderPendingDuplicates() : renderApprovedDuplicates()}

      <ConfirmModal
        show={showConfirmModal}
        title={`${
          modalData?.action === 'approve' ? 'Approve' :
          modalData?.action === 'reject' ? 'Reject' :
          modalData?.action === 'delete' ? 'Delete' :
          modalData?.action === 'remove' ? 'Remove' : 'Confirm'
        } ${modalData?.isBatch ? `${modalData.count} Duplicates` : 'Duplicate'}`}
        buttonTitle={
          modalData?.action === 'approve' ? 'Approve' :
          modalData?.action === 'reject' ? 'Reject' :
          modalData?.action === 'delete' ? 'Delete' :
          modalData?.action === 'remove' ? 'Remove' : 'Confirm'
        }
        onConfirm={() => {
          if (modalData) {
            if (modalData.isBatch) {
              handleBatchAction(modalData.action);
            } else {
              handleAction(
                modalData.duplicate.key,
                modalData.action,
                modalData.correctID,
                modalData.duplicateID
              );
            }
          }
        }}
        onClose={() => {
          setShowConfirmModal(false);
          setModalData(null);
        }}
        message={
          <div>
            {modalData?.isBatch ? (
              <p>
                Are you sure you want to {modalData.action} {modalData.count} duplicate{modalData.count > 1 ? 's' : ''}
                {modalData.action === 'approve' ? ' with their current mappings' : ''}?
              </p>
            ) : (
              <p>
                Are you sure you want to {modalData?.action} this duplicate{modalData?.action === 'approve' ? ' mapping' : ''}?
              </p>
            )}
            {modalData?.action === 'approve' && !modalData?.isBatch && (
              <div style={{ marginTop: '12px' }}>
                <p><strong>Mapping Details:</strong></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Correct ID: <code>{modalData.correctID}</code></li>
                  <li>Duplicate ID: <code>{modalData.duplicateID}</code></li>
                </ul>
                <p style={{ color: COLORS.text, fontSize: '14px' }}>
                  This will add the mapping to the active duplicates configuration 
                  and affect future data parsing.
                </p>
              </div>
            )}
            {modalData?.action === 'delete' && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ color: COLORS.alertRed, fontSize: '14px' }}>
                  <strong>Warning:</strong> This will permanently delete the pending duplicate record. 
                  This action cannot be undone.
                </p>
              </div>
            )}
            {modalData?.action === 'remove' && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ color: COLORS.alertRed, fontSize: '14px' }}>
                  <strong>Warning:</strong> This will remove the approved duplicate mapping from the configuration. 
                  Future parsing will treat these as separate items again.
                </p>
              </div>
            )}
          </div>
        }
      />
    </Container>
  );
};

export default DuplicateManagement;